import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { TrendingUp, Banknote, PiggyBank } from "lucide-react";

interface SavingsComparisonChartProps {
  title?: string;
  monatlicheEinzahlung?: number;
  laufzeitJahre?: number;
  sparbuchZins?: number;
  varianteAZins?: number;
  varianteBZins?: number;
  animated?: boolean;
  className?: string;
}

export function SavingsComparisonChart({
  title = "Sparformen im Vergleich",
  monatlicheEinzahlung = 100,
  laufzeitJahre = 18,
  sparbuchZins = 0.5,
  varianteAZins = 3,
  varianteBZins = 6,
  animated = true,
  className,
}: SavingsComparisonChartProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("de-AT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const data = useMemo(() => {
    const result = [];
    let sparbuch = 0;
    let varianteA = 0;
    let varianteB = 0;
    const jahresEinzahlung = monatlicheEinzahlung * 12;

    for (let jahr = 0; jahr <= laufzeitJahre; jahr++) {
      if (jahr > 0) {
        sparbuch = (sparbuch + jahresEinzahlung) * (1 + sparbuchZins / 100);
        varianteA = (varianteA + jahresEinzahlung) * (1 + varianteAZins / 100);
        varianteB = (varianteB + jahresEinzahlung) * (1 + varianteBZins / 100);
      }

      result.push({
        jahr,
        eingezahlt: jahresEinzahlung * jahr,
        sparbuch: Math.round(sparbuch),
        varianteA: Math.round(varianteA),
        varianteB: Math.round(varianteB),
      });
    }

    return result;
  }, [monatlicheEinzahlung, laufzeitJahre, sparbuchZins, varianteAZins, varianteBZins]);

  const endValues = data[data.length - 1];
  const totalEingezahlt = endValues?.eingezahlt || 0;

  const scenarios = [
    {
      label: "Sparbuch",
      zins: sparbuchZins,
      endwert: endValues?.sparbuch || 0,
      color: "hsl(var(--muted-foreground))",
      icon: <PiggyBank className="h-5 w-5" />,
    },
    {
      label: "Variante A",
      zins: varianteAZins,
      endwert: endValues?.varianteA || 0,
      color: "hsl(var(--primary))",
      icon: <Banknote className="h-5 w-5" />,
    },
    {
      label: "Variante B",
      zins: varianteBZins,
      endwert: endValues?.varianteB || 0,
      color: "hsl(142 76% 36%)",
      icon: <TrendingUp className="h-5 w-5" />,
    },
  ];

  return (
    <div className={cn("space-y-6", className)}>
      {title && <h3 className="font-semibold text-lg text-center">{title}</h3>}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        {scenarios.map((scenario, idx) => (
          <div
            key={scenario.label}
            className={cn(
              "p-4 rounded-lg text-center",
              animated && "animate-in fade-in slide-in-from-bottom-2",
              idx === 0 && "bg-muted/50",
              idx === 1 && "bg-primary/10",
              idx === 2 && "bg-emerald-100 dark:bg-emerald-950/30"
            )}
            style={animated ? { animationDelay: `${idx * 100}ms` } : undefined}
          >
            <div
              className="flex items-center justify-center mb-2"
              style={{ color: scenario.color }}
            >
              {scenario.icon}
            </div>
            <p className="text-xs text-muted-foreground">{scenario.label}</p>
            <p className="text-xs text-muted-foreground mb-1">
              {scenario.zins}% p.a.
            </p>
            <p className="font-bold text-lg" style={{ color: scenario.color }}>
              {formatCurrency(scenario.endwert)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              + {formatCurrency(scenario.endwert - totalEingezahlt)} Ertrag
            </p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorSparbuch" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorVarianteA" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorVarianteB" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(142 76% 36%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(142 76% 36%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="jahr"
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `${v}J`}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip
              formatter={(value: number) => formatCurrency(value)}
              labelFormatter={(label) => `Jahr ${label}`}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="sparbuch"
              name="Sparbuch"
              stroke="hsl(var(--muted-foreground))"
              fill="url(#colorSparbuch)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="varianteA"
              name="Variante A"
              stroke="hsl(var(--primary))"
              fill="url(#colorVarianteA)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="varianteB"
              name="Variante B"
              stroke="hsl(142 76% 36%)"
              fill="url(#colorVarianteB)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Info */}
      <p className="text-xs text-center text-muted-foreground">
        Annahme: {formatCurrency(monatlicheEinzahlung)}/Monat über {laufzeitJahre} Jahre
        • Eingezahlt: {formatCurrency(totalEingezahlt)}
      </p>
    </div>
  );
}
